import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { filter, Subscription } from 'rxjs';
import { PermissionsService } from '@core/services/permissions.service';
import { NavigationService } from '@core/services/navigation.service';
import { FooterComponent } from './components/footer/footer.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, FooterComponent, FontAwesomeModule],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly navigationSvc = inject(NavigationService);
  private readonly permissionsSvc = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly contentRef = viewChild<ElementRef<HTMLElement>>('content');
  private navigationSub?: Subscription;
  private lastModuleSegment?: string;
  private pendingModuleFade = false;

  readonly viewFadeIn = signal(false);

  readonly navItems = this.navigationSvc.getNavigationItems();
  readonly shell = this.navigationSvc.getAppShellConfig();

  collapse = false;
  mobileMenuOpen = false;
  userData: { initial: string; name: string } = { initial: 'U', name: 'Usuario' };

  private readonly mobileBreakpoint = 900;

  ngOnInit(): void {
    this.userData = this.permissionsSvc.getUserData;
    this.lastModuleSegment = this.readModuleSegment(this.router.url);
    this.playViewFadeIn();

    this.navigationSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const nextModule = this.readModuleSegment(event.url);

        if (nextModule !== this.lastModuleSegment) {
          this.pendingModuleFade = true;
          this.viewFadeIn.set(false);
          this.scrollContentToTop();
          this.cdr.markForCheck();
        }

        return;
      }

      if (event instanceof NavigationEnd && this.pendingModuleFade) {
        this.pendingModuleFade = false;
        this.lastModuleSegment = this.readModuleSegment(event.url);
        this.playViewFadeIn();
      }
    });
  }

  ngOnDestroy(): void {
    this.navigationSub?.unsubscribe();
  }

  get siderCollapsed(): boolean {
    return !this.isMobileViewport() && this.collapse;
  }

  toggleSidebar(): void {
    if (this.isMobileViewport()) {
      return;
    }

    this.collapse = !this.collapse;
    this.cdr.markForCheck();
  }

  toggleMobileMenu(): void {
    if (!this.isMobileViewport()) {
      return;
    }

    this.mobileMenuOpen = !this.mobileMenuOpen;

    if (this.mobileMenuOpen) {
      this.collapse = false;
    }

    this.cdr.markForCheck();
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.cdr.markForCheck();
  }

  onSidebarNavClick(): void {
    this.closeMobileMenu();
  }

  onLogout(): void {
    this.permissionsSvc.logout();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isMobileViewport()) {
      this.collapse = false;
    } else {
      this.mobileMenuOpen = false;
    }

    this.cdr.markForCheck();
  }

  private playViewFadeIn(): void {
    this.viewFadeIn.set(false);
    this.cdr.markForCheck();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.viewFadeIn.set(true);
        this.cdr.markForCheck();
      });
    });
  }

  private readModuleSegment(url: string): string | undefined {
    const path = url.split('?')[0].split('#')[0];
    const segment = path.split('/').filter(Boolean)[0];
    return segment || undefined;
  }

  private scrollContentToTop(): void {
    const content = this.contentRef()?.nativeElement;
    if (!content) {
      return;
    }

    content.scrollTop = 0;
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < this.mobileBreakpoint;
  }
}
